import React, { FC, useCallback, useState } from 'react';
import { getIn, useFormikContext } from 'formik';

import { isPresent } from 'Shared/utils';
import { STATE_OPTIONS } from 'Shared/constants/states';
import { Column, FormInput, Row, useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { reportError } from 'Shared/analytics';

import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import {
  transformLienholderAddress,
  LienHolderAddressResponseType,
} from 'QuoteApp/services/endpoints/GetLienholderAddress';
import {
  FormValuesType,
  LienHolderFieldsType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { useEdit, useFieldData } from 'QuoteApp/utils/contexts';
import { getFieldErrors } from 'QuoteApp/utils';
import { useAppContext } from 'QuoteApp/services/providers';

type LienHolderFormProps = {
  config: GetConfigResponseType['vehicles'];
  vehicleId: string;
};

const lienholderAddressReset = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  state: '',
};

export const LienHolderForm: FC<LienHolderFormProps> = ({
  config,
  vehicleId,
}) => {
  const {
    setFieldValue,
    handleChange,
    handleBlur,
    touched,
    errors,
    values,
    initialValues,
  } = useFormikContext<FormValuesType>();

  const { toast } = useToast();
  const { editingId } = useEdit();
  const { alwaysShowErrors, serverErrors, editableFields } = useFieldData();
  const readOnly = editingId !== vehicleId;

  const { config: appConfig } = useAppContext();
  const ownershipLabel = appConfig?.accessEnabled
    ? 'Vehicle ownership status'
    : 'Ownership';

  const { askRegisteredToApplicant } = appConfig?.vehicles?.ownership || {
    askRegisteredToApplicant: true,
  };

  const getFullFieldName = useCallback(
    (fieldName: keyof LienHolderFieldsType) =>
      `vehicles.${vehicleId}.lienHolder.${fieldName}`,
    [vehicleId]
  );

  const getInputProps = useCallback(
    (
      fieldName: keyof LienHolderFieldsType,
      required = true,
      disabledConditions = false
    ) => {
      const fullFieldName = getFullFieldName(fieldName);
      return {
        name: fullFieldName,
        onChange: handleChange,
        onBlur: handleBlur,
        value: values.vehicles[vehicleId].lienHolder[fieldName],
        disabled:
          readOnly ||
          !getIn(editableFields, fullFieldName) ||
          disabledConditions,
        errorMessage: getFieldErrors<FormValuesType>(
          fullFieldName,
          required,
          alwaysShowErrors,
          errors,
          serverErrors,
          values,
          touched,
          initialValues
        ),
      };
    },
    [
      editableFields,
      initialValues,
      alwaysShowErrors,
      readOnly,
      errors,
      serverErrors,
      getFullFieldName,
      handleBlur,
      handleChange,
      touched,
      values,
      vehicleId,
    ]
  );

  const [
    { loading: loadingLienholderAddresses },
    requestLienholderAddress,
  ] = useLazyQuoteApi<LienHolderAddressResponseType>({
    url: '/insurance_products/lienholder_address',
    transformResponse: transformLienholderAddress,
  });

  const [disableLienholderInputs, setDisableLienholderInputs] = useState<
    boolean
  >(() => !!config.lienHolders[values.vehicles[vehicleId].lienHolder.name]);

  return (
    <>
      <Row>
        {askRegisteredToApplicant && (
          <Column variant="third">
            <FormInput
              data-testid="vehicle-registration"
              name={`vehicles.${vehicleId}.registeredToApplicant`}
              onBlur={handleBlur}
              value={values.vehicles[vehicleId].registeredToApplicant}
              errorMessage={getFieldErrors<FormValuesType>(
                `vehicles.${vehicleId}.registeredToApplicant`,
                true,
                alwaysShowErrors,
                errors,
                serverErrors,
                values,
                touched
              )}
              variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
              label="Is applicant or spouse the registered owner/lessee or will be in 30 days?"
              tooltip="If this vehicle is registered to a business or trust, please select No."
              disabled={
                editingId !== vehicleId ||
                !getIn(
                  editableFields,
                  `vehicles.${vehicleId}.registeredToApplicant`
                )
              }
              onChange={(value: string) => {
                setFieldValue(
                  `vehicles.${vehicleId}.registeredToApplicant`,
                  value
                );
              }}
              options={{ true: 'Yes', false: 'No' }}
            />
          </Column>
        )}
        <Column variant="third">
          <FormInput
            name={`vehicles.${vehicleId}.ownershipType`}
            onBlur={handleBlur}
            value={values.vehicles[vehicleId].ownershipType}
            errorMessage={getFieldErrors<FormValuesType>(
              `vehicles.${vehicleId}.ownershipType`,
              true,
              alwaysShowErrors,
              errors,
              serverErrors,
              values,
              touched
            )}
            label={ownershipLabel}
            variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
            disabled={
              editingId !== vehicleId ||
              !getIn(editableFields, `vehicles.${vehicleId}.ownershipType`)
            }
            data-testid="vehicle-ownership"
            onChange={(value: string) => {
              setFieldValue(`vehicles.${vehicleId}.ownershipType`, value);
            }}
            options={config.ownership.ownershipType}
          />
        </Column>
        <Column />
      </Row>
      {['leased', 'financed'].includes(
        values.vehicles[vehicleId].ownershipType
      ) && (
        <div data-testid="lienholder-form">
          <Row>
            <Column variant="third">
              <FormInput
                data-testid="lien-holder-name"
                {...getInputProps('name')}
                variant={readOnly ? 'readOnly' : 'combobox'}
                label="Lienholder Name"
                onChange={async (value: string) => {
                  const lienholderInConfig = !!config.lienHolders[value];
                  const previousValueInConfig = disableLienholderInputs;

                  if (lienholderInConfig) {
                    // if we are using the config then request the address
                    try {
                      const lienholderAddress = await requestLienholderAddress({
                        params: {
                          lienholder_name: value,
                        },
                      });

                      // check if the actual response address is valid
                      if (!lienholderAddress.data.isValid) {
                        throw new Error('invalid lienholder address');
                      }

                      setFieldValue(`vehicles.${vehicleId}.lienHolder`, {
                        name: value,
                        ...lienholderAddress.data.address,
                      });

                      // disable the inputs when we have a successful lineholder address
                      setDisableLienholderInputs(true);
                    } catch (e) {
                      // temporary backwards compatability to be removed after backend deploy
                      if (isPresent(config.lienHolderAddresses[value])) {
                        setFieldValue(`vehicles.${vehicleId}.lienHolder`, {
                          name: value,
                          ...transformLienholderAddress(
                            config.lienHolderAddresses[value]
                          ).address,
                        });
                      } else {
                        // reset fields on error
                        setFieldValue(`vehicles.${vehicleId}.lienHolder`, {
                          name: value,
                          ...lienholderAddressReset,
                        });

                        // enable the inputs
                        setDisableLienholderInputs(false);

                        toast({
                          type: 'error',
                          message:
                            'Address not found. Please enter lienholder address',
                          withSecondaryNav: false,
                          icon: 'error',
                        });

                        // send error to analytics
                        reportError(e, 'aqui-lienholder-address-not-found', {
                          lienholderName: value,
                        });
                      }
                    }
                  } else if (previousValueInConfig) {
                    // if the previous value was in config but changed to custom then reset the fields
                    setFieldValue(`vehicles.${vehicleId}.lienHolder`, {
                      name: value,
                      ...lienholderAddressReset,
                    });

                    // enable the inputs
                    setDisableLienholderInputs(false);
                  } else {
                    // only overwrite the name field if switching from custom value to different custom value
                    setFieldValue(
                      `vehicles.${vehicleId}.lienHolder.name`,
                      value
                    );
                  }
                }}
                options={config.lienHolders}
              />
            </Column>
            <Column variant="third">
              <FormInput
                data-testid="lien-holder-address-line-one"
                {...getInputProps(
                  'addressLine1',
                  true,
                  disableLienholderInputs
                )}
                variant={readOnly ? 'readOnly' : 'text'}
                label="Lienholder Address"
                loading={loadingLienholderAddresses}
              />
            </Column>
            <Column variant="third">
              <FormInput
                data-testid="lien-holder-address-line-two"
                {...getInputProps(
                  'addressLine2',
                  false,
                  disableLienholderInputs
                )}
                variant={readOnly ? 'readOnly' : 'text'}
                label="Lienholder Address 2"
                loading={loadingLienholderAddresses}
              />
            </Column>
          </Row>
          <Row>
            <Column variant="third">
              <FormInput
                data-testid="lien-holder-city"
                {...getInputProps('city', true, disableLienholderInputs)}
                variant={readOnly ? 'readOnly' : 'text'}
                label="City"
                loading={loadingLienholderAddresses}
              />
            </Column>
            <Column variant="third">
              <FormInput
                data-testid="lien-holder-state"
                {...getInputProps('state', true, disableLienholderInputs)}
                variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                label="State"
                onChange={(value: string) =>
                  setFieldValue(getFullFieldName('state'), value)
                }
                options={STATE_OPTIONS}
                loading={loadingLienholderAddresses}
              />
            </Column>
            <Column variant="third">
              <FormInput
                data-testid="lien-holder-postal-code"
                {...getInputProps('postalCode', true, disableLienholderInputs)}
                variant={readOnly ? 'readOnly' : 'text'}
                label="ZIP Code"
                loading={loadingLienholderAddresses}
              />
            </Column>
          </Row>
        </div>
      )}
    </>
  );
};
